import {
    Alert,
    Button,
    Checkbox,
    FormControlLabel,
    TextField,
    IconButton,
    InputAdornment
} from "@mui/material";
import { Visibility, VisibilityOff, Email, Lock } from "@mui/icons-material";
import React, { useContext, useEffect, useState } from "react";
import { User } from "../Models/User";
import { LoginContext } from "../contexts/LoginContext";
import { EMAIL_REG_EXPRESSION, LOGIN_URL, USER_STORAGE_KEY } from "../utils/constant";
import "../styles/Login.css";
import useAxios from "../hooks/useAxios";
import { LoginResponse } from "../interface/ApiTypes.interface";
import { useNavigate } from "react-router-dom";
import { logger } from "../utils/logger";

export function Login() {
    const { setCurrentUser, currentUser } = useContext(LoginContext);
    const navigate = useNavigate();
    const [formData, setFormData] = useState({ email: "", password: "" });
    const [error, setError] = useState({ email: "", password: "" });
    const [globalError, setGlobalError] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { postWithAxios } = useAxios();
    const [view, setView] = useState<
        "login" | "resetPassword" | "resendActivation"
    >("login");
    const [activationEmail, setActivationEmail] = useState("");
    const [activationMessage, setActivationMessage] = useState("");
    const [resetEmail, setResetEmail] = useState("");
    const [resetMessage, setResetMessage] = useState("");

    // Rate limiting states
    const [attemptCount, setAttemptCount] = useState(0);
    const [lastAttemptTime, setLastAttemptTime] = useState<number>(0);
    const [isBlocked, setIsBlocked] = useState(false);
    const [secondsUntilUnblock, setSecondsUntilUnblock] = useState(0);

    // Messages d'information - détection au chargement
    const [infoMessage, setInfoMessage] = useState<{text: string, severity: "warning" | "info"} | null>(null);

    const validateForm = () => {
        setError({
            email:
                formData.email.trim().length <= 0
                    ? "Ce champ est requis"
                    : !formData.email.match(EMAIL_REG_EXPRESSION)
                        ? "Email non valide"
                        : "",
            password:
                formData.password.trim().length <= 0 ? "Ce champ est requis" : ""
        });
    };

    const handleOnChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const login = async () => {
        // Vérifier si bloqué par rate limiting
        if (isBlocked) {
            setGlobalError(`Trop de tentatives. Veuillez patienter ${secondsUntilUnblock} seconde(s).`);
            return;
        }

        if (
            error.email ||
            error.password ||
            !formData.email ||
            !formData.password
        ) {
            return;
        }

        // Vérifier le rate limiting (3 tentatives par minute)
        const now = Date.now();
        const oneMinute = 60 * 1000;

        // Si plus d'une minute s'est écoulée, réinitialiser le compteur
        if (now - lastAttemptTime > oneMinute) {
            setAttemptCount(1);
            setLastAttemptTime(now);
        } else {
            const newCount = attemptCount + 1;
            setAttemptCount(newCount);
            setLastAttemptTime(now);

            // Si 3 tentatives ou plus dans la même minute, bloquer
            if (newCount >= 3) {
                setIsBlocked(true);
                setSecondsUntilUnblock(60);
                setGlobalError("Trop de tentatives. Veuillez patienter 60 secondes.");
                return;
            }
        }

        setIsLoading(true);
        try {
            const response = (await postWithAxios(LOGIN_URL, {
                email: formData.email,
                password: formData.password
            })) as LoginResponse;

            setGlobalError("");
            // Réinitialiser le compteur en cas de succès
            setAttemptCount(0);
            setLastAttemptTime(0);

            setCurrentUser(
                new User({
                    uuid: response.user.uuid,
                    email: response.user.email,
                    role: response.user.role,
                    isActivated: response.user.isActivated,
                    firstname: response.user.firstname,
                    lastname: response.user.lastname,
                    projects: response.user.projects,
                    defaultProject: response.user.defaultProject,
                    tokens: {
                        accessToken: response.accessToken,
                        refreshToken: response.refreshToken
                    }
                })
            );
        } catch (err: any) {
            // Utiliser le code métier pour déterminer le message d'erreur
            // AUTH_FAILED = code du backend, UNAUTHORIZED = fallback générique pour 401
            const isAuthError = ['AUTH_FAILED', 'UNAUTHORIZED'].includes(err.code);
            const errorMessage = isAuthError
                ? "Identifiants incorrects ou compte non activé. Vérifiez vos informations ou activez votre compte."
                : err.message || 'Une erreur est survenue';
            setGlobalError(errorMessage);
            setCurrentUser(undefined);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            login();
        }
    };

    const handlePasswordReset = async () => {
        if (!resetEmail.match(EMAIL_REG_EXPRESSION)) {
            setGlobalError("Email non valide");
            return;
        }
        setIsLoading(true);
        setGlobalError("");
        setResetMessage("");
        try {
            await postWithAxios("/auth/forgot-password", { email: resetEmail });
            setResetMessage(
                "Si un compte avec cet email existe, un lien de réinitialisation de mot de passe a été envoyé."
            );
        } catch {
            setResetMessage(
                "Si un compte avec cet email existe, un lien de réinitialisation de mot de passe a été envoyé."
            );
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendActivation = async () => {
        if (!activationEmail.match(EMAIL_REG_EXPRESSION)) {
            setGlobalError("Email non valide");
            return;
        }
        setIsLoading(true);
        setGlobalError("");
        setActivationMessage("");

        try {
            await postWithAxios('/auth/resend-activation', { email: activationEmail });
            setActivationMessage("Si ce compte existe et n'est pas activé, un email d'activation a été renvoyé.");
        } catch (err: any) {
            setActivationMessage("Si ce compte existe et n'est pas activé, un email d'activation a été renvoyé.");
        } finally {
            setIsLoading(false);
        }
    };

    // Gérer le décompte du rate limiting
    useEffect(() => {
        if (isBlocked && secondsUntilUnblock > 0) {
            const timer = setTimeout(() => {
                setSecondsUntilUnblock(secondsUntilUnblock - 1);
            }, 1000);

            return () => clearTimeout(timer);
        } else if (isBlocked && secondsUntilUnblock === 0) {
            // Débloquer après 60 secondes
            setIsBlocked(false);
            setAttemptCount(0);
            setGlobalError("");
        }
    }, [isBlocked, secondsUntilUnblock]);

    // Détecter si l'utilisateur a été redirigé (session expirée ou accès protégé)
    useEffect(() => {
        const sessionExpired = sessionStorage.getItem('sessionExpired');
        const loginRequired = sessionStorage.getItem('loginRequired');

        if (sessionExpired === 'true') {
            setInfoMessage({
                text: "Votre session a expiré. Veuillez vous reconnecter.",
                severity: "warning"
            });
            sessionStorage.removeItem('sessionExpired');
        } else if (loginRequired === 'true') {
            setInfoMessage({
                text: "Veuillez vous connecter pour accéder à cette page.",
                severity: "info"
            });
            sessionStorage.removeItem('loginRequired');
        }
    }, []);

    useEffect(() => {
        validateForm();
    }, [formData]);

    useEffect(() => {
        if (!currentUser) return;

        try {
            switch (currentUser?.isAdmin()) {
                case true:
                    navigate("/admin/dashboard");
                    break;
                case false:
                    navigate("/user/dashboard");
                    break;
            }
        } catch (error) {
            logger.error("Error during redirect:", error);
            // En cas d'erreur, nettoyer le localStorage
            localStorage.removeItem(USER_STORAGE_KEY);
            setCurrentUser(undefined);
        }
    }, [currentUser, navigate, setCurrentUser]);

    return (
        <div className="login-container">
            <div className="login-card">
                {view === "login" && (
                    <>
                        <div className="login-header">
                            <h1 className="login-title">T.A.S</h1>
                            <p className="login-subtitle">
                                Connectez-vous à votre espace personnel pour accéder à vos projets et suivre vos contributions
                            </p>
                        </div>

                        {infoMessage && (
                            <Alert severity={infoMessage.severity} className="error-alert" onClose={() => setInfoMessage(null)}>
                                {infoMessage.text}
                            </Alert>
                        )}

                        {globalError && (
                            <Alert severity="error" className="error-alert">
                                {globalError}
                            </Alert>
                        )}

                        <div className="form-group">
                            <TextField
                                label="Adresse email"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleOnChange}
                                onKeyDown={handleKeyDown}
                                error={!!error.email}
                                helperText={error.email}
                                disabled={isLoading}
                                className="login-field"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Email />
                                        </InputAdornment>
                                    )
                                }}
                                FormHelperTextProps={{
                                    className: "field-error"
                                }}
                            />

                            <TextField
                                label="Mot de passe"
                                name="password"
                                type={showPassword ? "text" : "password"}
                                value={formData.password}
                                onChange={handleOnChange}
                                onKeyDown={handleKeyDown}
                                error={!!error.password}
                                helperText={error.password}
                                disabled={isLoading}
                                className="login-field"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Lock />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowPassword(!showPassword)}
                                                edge="end"
                                                disabled={isLoading}
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                                FormHelperTextProps={{
                                    className: "field-error"
                                }}
                            />

                            <div className="form-inline-actions">
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={rememberMe}
                                            onChange={(e) => setRememberMe(e.target.checked)}
                                            name="rememberMe"
                                        />
                                    }
                                    label="Se souvenir de moi"
                                />

                                <Button
                                    variant="text"
                                    size="small"
                                    onClick={() => setView("resetPassword")}
                                    className="forgot-password-btn"
                                >
                                    Mot de passe oublié ?
                                </Button>
                            </div>
                        </div>

                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            className="login-btn"
                            onClick={login}
                            disabled={isLoading || isBlocked}
                        >
                            {isLoading
                                ? "Connexion..."
                                : isBlocked
                                    ? `Bloqué (${secondsUntilUnblock}s)`
                                    : "Se connecter"}
                        </Button>

                        <div className="helper-text">
                            <p>
                                C’est votre première connexion ?
                                <Button
                                    variant="text"
                                    size="small"
                                    className="link-btn"
                                    onClick={() => setView("resendActivation")}
                                >
                                    Activer votre compte
                                </Button>
                            </p>
                        </div>
                    </>
                )}

                {view === "resetPassword" && (
                    <>
                        <div className="login-header">
                            <h1 className="login-title">Mot de passe oublié</h1>
                            <p className="login-subtitle">
                                Entrez votre email pour recevoir un lien de réinitialisation.
                            </p>
                        </div>

                        {globalError && (
                            <Alert severity="error" className="error-alert">
                                {globalError}
                            </Alert>
                        )}
                        {resetMessage && (
                            <Alert severity="success" className="success-alert">
                                {resetMessage}
                            </Alert>
                        )}

                        <div className="form-group">
                            <TextField
                                label="Email"
                                name="resetEmail"
                                value={resetEmail}
                                onChange={(e) => setResetEmail(e.target.value)}
                                fullWidth
                                required
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Email />
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </div>

                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            className="login-btn"
                            onClick={handlePasswordReset}
                            disabled={isLoading}
                        >
                            {isLoading ? "Envoi en cours..." : "Envoyer le lien"}
                        </Button>

                        <Button
                            variant="text"
                            size="small"
                            onClick={() => {
                                setView("login");
                                setGlobalError("");
                                setResetMessage("");
                            }}
                            className="forgot-password-btn"
                            style={{ marginTop: "1rem" }}
                        >
                            Retour à la connexion
                        </Button>
                    </>
                )}

                {view === "resendActivation" && (
                    <>
                        <div className="login-header">
                            <h1 className="login-title">Activer mon compte</h1>
                            <p className="login-subtitle">
                                Entrez votre email pour recevoir un nouveau lien d’activation.
                            </p>
                        </div>

                        {globalError && (
                            <Alert severity="error" className="error-alert">
                                {globalError}
                            </Alert>
                        )}
                        {activationMessage && (
                            <Alert severity="success" className="success-alert">
                                {activationMessage}
                            </Alert>
                        )}

                        <div className="form-group">
                            <TextField
                                label="Email"
                                name="activationEmail"
                                value={activationEmail}
                                onChange={(e) => setActivationEmail(e.target.value)}
                                fullWidth
                                required
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Email />
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </div>

                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            className="login-btn"
                            onClick={handleResendActivation}
                            disabled={isLoading}
                        >
                            {isLoading ? "Envoi en cours..." : "Renvoyer le lien"}
                        </Button>

                        <Button
                            variant="text"
                            size="small"
                            onClick={() => {
                                setView("login");
                                setGlobalError("");
                                setActivationMessage("");
                            }}
                            className="forgot-password-btn"
                            style={{ marginTop: "1rem" }}
                        >
                            Retour à la connexion
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
}
